<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import {
  CalendarDaysIcon,
  ChartBarSquareIcon,
  PencilSquareIcon,
  PlusCircleIcon,
  TrashIcon,
  TruckIcon,
  XMarkIcon,
} from "@heroicons/vue/24/outline";
import ProductService from "@/services/ProductService";
import CurrencyInput from "@/components/CurrencyInput.vue";
import PricingPeriodPriceChart from "@/components/PricingPeriodPriceChart.vue";

const productService = new ProductService();
const MIN_PRICE_PERIOD_DATE = "2000-01-01";
const DEFAULT_INTERNAL_HOURLY_RATE = 1800;
const DEFAULT_INTERNAL_DAILY_RATE = 14400;
const PRICE_HISTORY_METRICS = {
  fixed_fee: "Fixed fee",
  hourly_rate: "Sell hourly rate",
  daily_rate: "Sell daily rate",
  internal_hourly_rate: "Internal hourly pay",
  internal_daily_rate: "Internal daily pay",
};
const products = ref([]);
const pricingDateRanges = ref([]);
const vehicleTypes = ref([]);
const loading = ref(false);
const error = ref(null);
const toast = reactive({
  visible: false,
  message: "",
  tone: "success",
});
const savingKey = ref(null);
const deletingKey = ref(null);
const savingVehicleProductId = ref(null);
const expandedHistoryByProduct = ref({});
const activePricingProductId = ref(null);
const activeVehicleProductId = ref(null);
const activePriceHistoryProductId = ref(null);
const activePriceHistoryMetric = ref("hourly_rate");
const logisticsModalOpen = ref(false);
const dateRangeModalOpen = ref(false);
const dateRangeSaving = ref(false);
const dateRangeError = ref(null);
const editingDateRangeId = ref(null);
const newDateRange = reactive({
  starts_at: "",
  ends_at: "",
  percentage_increase: 0,
});
const logisticsPricing = reactive({
  base_address: "",
  fuel_rate_per_mile: 0,
  travel_driver_hourly_rate: 0,
  accommodation_room_nightly_rate: 0,
  admin_fee: 0,
  currency: "GBP",
});
const logisticsSaving = ref(false);
let toastTimeout = null;

const moneyFormatter = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
});

const showToast = (message, tone = "success") => {
  toast.message = message;
  toast.tone = tone;
  toast.visible = true;
  if (toastTimeout) {
    clearTimeout(toastTimeout);
  }
  toastTimeout = setTimeout(() => {
    toast.visible = false;
  }, 2500);
};

const toDateInputValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const exactDate = /^\d{4}-\d{2}-\d{2}$/;
    if (exactDate.test(value)) {
      return value;
    }

    const prefixedDate = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (prefixedDate) {
      return prefixedDate[1];
    }
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return parsed.toISOString().slice(0, 10);
};

const formatDate = (value) => {
  const normalized = toDateInputValue(value);
  if (!normalized) return "Open";
  const [year, month, day] = normalized.split("-");
  return `${day}/${month}/${year}`;
};

const isCompleteIsoDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value ?? "");

const isValidPricePeriodDate = (value) => {
  if (!isCompleteIsoDate(value) || value < MIN_PRICE_PERIOD_DATE) {
    return false;
  }

  const [year, month, day] = value.split("-").map((part) => Number(part));
  const parsed = new Date(Date.UTC(year, month - 1, day));

  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
};

const formatMoney = (value) => moneyFormatter.format(Number(value ?? 0) / 100);

const formatRatePair = (hourlyRate, dailyRate) => {
  const parts = [];
  if (Number(hourlyRate ?? 0) > 0) {
    parts.push(`${formatMoney(hourlyRate)}/hr`);
  }
  if (Number(dailyRate ?? 0) > 0) {
    parts.push(`${formatMoney(dailyRate)}/day`);
  }

  return parts.length > 0 ? parts.join(" / ") : "Not set";
};

const sortPeriodsByStartDate = (periods = []) =>
  [...periods].sort((a, b) => {
    if (!a.starts_at) return 1;
    if (!b.starts_at) return -1;
    return a.starts_at.localeCompare(b.starts_at);
  });

const makeTempId = (prefix) =>
  `temp-${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const getActionKey = (scope, period) => `${scope}:${period.id}`;
const isSaving = (scope, period) =>
  savingKey.value === getActionKey(scope, period);
const isDeleting = (scope, period) =>
  deletingKey.value === getActionKey(scope, period);

const normalizeLogisticsPricing = (payload = {}) => ({
  base_address: payload.base_address ?? "",
  fuel_rate_per_mile: payload.fuel_rate_per_mile ?? 0,
  travel_driver_hourly_rate: payload.travel_driver_hourly_rate ?? 0,
  accommodation_room_nightly_rate: payload.accommodation_room_nightly_rate ?? 0,
  admin_fee: payload.admin_fee ?? 0,
  currency: payload.currency ?? "GBP",
});

const normalizePricePeriod = (period) => ({
  ...period,
  starts_at: toDateInputValue(period.starts_at),
  ends_at: toDateInputValue(period.ends_at),
  starts_at_draft: toDateInputValue(period.starts_at),
  ends_at_draft: toDateInputValue(period.ends_at),
  currency: period.currency ?? "GBP",
  fixed_fee: period.fixed_fee ?? 0,
  hourly_rate: period.hourly_rate ?? 0,
  daily_rate: period.daily_rate ?? 0,
  internal_hourly_rate:
    Number(period.internal_hourly_rate ?? 0) > 0
      ? period.internal_hourly_rate
      : DEFAULT_INTERNAL_HOURLY_RATE,
  internal_daily_rate:
    Number(period.internal_daily_rate ?? 0) > 0
      ? period.internal_daily_rate
      : DEFAULT_INTERNAL_DAILY_RATE,
  dirty: false,
  isNew: false,
});

const normalizePricingDateRange = (dateRange) => ({
  ...dateRange,
  starts_at: toDateInputValue(dateRange.starts_at),
  ends_at: toDateInputValue(dateRange.ends_at),
  percentage_increase: Number(dateRange.percentage_increase ?? 0),
  product_prices_count: Number(dateRange.product_prices_count ?? 0),
  product_count: Number(dateRange.product_count ?? 0),
});

const normalizeRequirementPayPeriod = (period) => ({
  ...period,
  product_price_id: period.product_price_id,
  currency: period.currency ?? "GBP",
  hourly_rate: period.hourly_rate ?? 0,
  daily_rate: period.daily_rate ?? 0,
  dirty: false,
  isNew: false,
});

const normalizeVehicleRequirement = (payload = null, productId = null) => ({
  id: payload?.id ?? null,
  product_id: payload?.product_id ?? productId,
  vehicle_type_id: payload?.vehicle_type_id ?? "",
  vehicle_type: payload?.vehicle_type ?? "",
  required_count: Number(payload?.required_count ?? 0),
  dirty: false,
});

const buildRequirementPayPlaceholder = (productPricePeriod) => ({
  id: makeTempId(`requirement-${productPricePeriod.id}`),
  product_price_id: productPricePeriod.id,
  currency: productPricePeriod.currency ?? "GBP",
  hourly_rate: 0,
  daily_rate: 0,
  dirty: false,
  isNew: true,
});

const alignRequirementPayPeriods = (requirement, productPricePeriods) => {
  const existing = new Map(
    (requirement.pay_periods ?? requirement.payPeriods ?? [])
      .map((period) => normalizeRequirementPayPeriod(period))
      .map((period) => [String(period.product_price_id), period]),
  );

  return (productPricePeriods ?? []).map((productPricePeriod) => {
    return (
      existing.get(String(productPricePeriod.id)) ??
      buildRequirementPayPlaceholder(productPricePeriod)
    );
  });
};

const normalizeRequirement = (requirement, productPricePeriods) => ({
  ...requirement,
  pay_periods: alignRequirementPayPeriods(requirement, productPricePeriods),
});

const normalizeProduct = (product) => ({
  ...product,
  ...(() => {
    const pricePeriods = sortPeriodsByStartDate(
      (product.price_periods ?? []).map((period) =>
        normalizePricePeriod(period),
      ),
    );

    return {
      price_periods: pricePeriods,
      personnel_requirements: (product.personnel_requirements ?? []).map(
        (requirement) => normalizeRequirement(requirement, pricePeriods),
      ),
      vehicle_requirements: normalizeVehicleRequirement(
        product.vehicle_requirements ?? product.vehicleRequirements ?? null,
        product.id,
      ),
    };
  })(),
});

const loadProducts = async () => {
  loading.value = true;
  error.value = null;
  try {
    const [
      productResponse,
      logisticsResponse,
      vehicleTypeResponse,
      dateRangeResponse,
    ] = await Promise.all([
      productService.pricingIndex(),
      productService.logisticsPricing(),
      productService.vehicleTypes(),
      productService.pricingDateRanges(),
    ]);
    products.value = productResponse.map((product) =>
      normalizeProduct(product),
    );
    Object.assign(
      logisticsPricing,
      normalizeLogisticsPricing(logisticsResponse),
    );
    vehicleTypes.value = vehicleTypeResponse ?? [];
    pricingDateRanges.value = (dateRangeResponse ?? []).map((dateRange) =>
      normalizePricingDateRange(dateRange),
    );
  } catch (err) {
    console.error(err);
    error.value =
      err.response?.data?.message ?? "Unable to load pricing at the moment.";
  } finally {
    loading.value = false;
  }
};

const replaceProduct = (payload) => {
  const index = products.value.findIndex((p) => p.id === payload.id);
  const normalized = normalizeProduct(payload);
  if (index === -1) {
    products.value.push(normalized);
  } else {
    products.value[index] = normalized;
  }
};

const addPeriod = (product) => {
  const newPeriod = {
    id: makeTempId("product"),
    starts_at: "",
    ends_at: "",
    starts_at_draft: "",
    ends_at_draft: "",
    currency: "GBP",
    fixed_fee: 0,
    hourly_rate: 0,
    daily_rate: 0,
    internal_hourly_rate: DEFAULT_INTERNAL_HOURLY_RATE,
    internal_daily_rate: DEFAULT_INTERNAL_DAILY_RATE,
    dirty: true,
    isNew: true,
  };

  product.price_periods = [...product.price_periods, newPeriod];

  for (const requirement of product.personnel_requirements ?? []) {
    requirement.pay_periods = [
      ...(requirement.pay_periods ?? []),
      buildRequirementPayPlaceholder(newPeriod),
    ];
  }
};

const markDirty = (period) => {
  period.dirty = true;
};

const dateDraftKey = (field) => `${field}_draft`;

const commitDateDraft = (period, field) => {
  const draftKey = dateDraftKey(field);
  const draftValue = period[draftKey] ?? "";

  if (field === "ends_at" && draftValue === "") {
    if ((period.ends_at ?? "") !== "") {
      period.ends_at = "";
      markDirty(period);
    }
    return true;
  }

  if (!isValidPricePeriodDate(draftValue)) {
    return false;
  }

  if ((period[field] ?? "") !== draftValue) {
    period[field] = draftValue;
    markDirty(period);
  }

  return true;
};

const updateDateDraft = (period, field, value) => {
  period[dateDraftKey(field)] = value;

  if (field === "ends_at" && value === "") {
    commitDateDraft(period, field);
    return;
  }

  if (isValidPricePeriodDate(value)) {
    commitDateDraft(period, field);
  }
};

const syncDateDraftsForPeriod = (period) => {
  if (!commitDateDraft(period, "starts_at")) {
    error.value = `Enter a valid start date on or after ${formatDate(MIN_PRICE_PERIOD_DATE)}.`;
    return false;
  }

  if (!commitDateDraft(period, "ends_at")) {
    error.value = `Enter a valid end date on or after ${formatDate(MIN_PRICE_PERIOD_DATE)}, or leave it blank.`;
    return false;
  }

  if (period.ends_at && period.ends_at < period.starts_at) {
    error.value = "End date cannot be before the start date.";
    return false;
  }

  return true;
};

const buildPricePeriodPayload = (period) => ({
  ...(period.isNew ? {} : { id: period.id }),
  fixed_fee: period.fixed_fee ?? 0,
  hourly_rate: period.hourly_rate ?? 0,
  daily_rate: period.daily_rate ?? 0,
  internal_hourly_rate: period.internal_hourly_rate ?? 0,
  internal_daily_rate: period.internal_daily_rate ?? 0,
  currency: period.currency ?? "GBP",
  starts_at: period.starts_at,
  ends_at: period.ends_at || null,
});

const buildProductPricePeriodsPayload = (product) => {
  for (const period of product.price_periods ?? []) {
    if (!syncDateDraftsForPeriod(period)) {
      return null;
    }
  }

  return {
    periods: (product.price_periods ?? []).map((period) =>
      buildPricePeriodPayload(period),
    ),
  };
};

const dirtyRequirementPayDraftsForPeriod = (product, period) => {
  const drafts = [];

  for (const requirement of product.personnel_requirements ?? []) {
    const slot = getRequirementPayPeriod(requirement, period.id);
    if (!slot || !slot.dirty) {
      continue;
    }

    drafts.push({
      requirementId: requirement.id,
      hourly_rate: slot.hourly_rate ?? 0,
      daily_rate: slot.daily_rate ?? 0,
      currency: slot.currency ?? "GBP",
    });
  }

  return drafts;
};

const hasDirtyRequirementPayForPeriod = (product, period) =>
  dirtyRequirementPayDraftsForPeriod(product, period).length > 0;

const canSavePeriodRow = (product, period) =>
  Boolean(period?.dirty || hasDirtyRequirementPayForPeriod(product, period));

const resolveSavedPeriodId = (product, payload) => {
  const periods = product?.price_periods ?? [];
  const payloadEndAt = payload.ends_at ?? "";

  const exactMatches = periods.filter((candidate) => {
    return (
      candidate.starts_at === payload.starts_at &&
      (candidate.ends_at ?? "") === payloadEndAt &&
      (candidate.fixed_fee ?? 0) === (payload.fixed_fee ?? 0) &&
      (candidate.hourly_rate ?? 0) === (payload.hourly_rate ?? 0) &&
      (candidate.daily_rate ?? 0) === (payload.daily_rate ?? 0) &&
      (candidate.internal_hourly_rate ?? 0) ===
        (payload.internal_hourly_rate ?? 0) &&
      (candidate.internal_daily_rate ?? 0) ===
        (payload.internal_daily_rate ?? 0) &&
      (candidate.currency ?? "GBP") === (payload.currency ?? "GBP")
    );
  });

  if (exactMatches.length > 0) {
    return exactMatches[exactMatches.length - 1].id;
  }

  const dateMatches = periods.filter((candidate) => {
    return (
      candidate.starts_at === payload.starts_at &&
      (candidate.ends_at ?? "") === payloadEndAt
    );
  });

  return dateMatches[dateMatches.length - 1]?.id ?? null;
};

const savePeriod = async (product, period) => {
  const pricePeriodsPayload = period.dirty
    ? buildProductPricePeriodsPayload(product)
    : null;
  if (period.dirty && !pricePeriodsPayload) {
    return;
  }

  const periodPayload = buildPricePeriodPayload(period);
  const dirtyRequirementDrafts = dirtyRequirementPayDraftsForPeriod(
    product,
    period,
  );

  for (const draft of dirtyRequirementDrafts) {
    if ((draft.hourly_rate ?? 0) <= 0 && (draft.daily_rate ?? 0) <= 0) {
      error.value = "Set an hourly or daily pay rate before saving.";
      return;
    }
  }

  savingKey.value = getActionKey("product", period);
  error.value = null;

  try {
    const wasNewPeriod = period.isNew;
    const originalPeriodId = period.id;
    let updatedProduct;

    if (period.dirty) {
      updatedProduct = await productService.updateProductPricePeriods(
        product.id,
        pricePeriodsPayload,
      );
      replaceProduct(updatedProduct);
    }

    let targetPeriodId = originalPeriodId;
    if (wasNewPeriod && dirtyRequirementDrafts.length > 0) {
      const refreshedProduct = products.value.find(
        (candidate) => String(candidate.id) === String(product.id),
      );
      targetPeriodId = resolveSavedPeriodId(refreshedProduct, periodPayload);

      if (!targetPeriodId) {
        showToast(
          "Sell period saved, but staff pay could not be auto-linked. Save once more to apply staff pay.",
          "error",
        );
        return;
      }
    }

    for (const draft of dirtyRequirementDrafts) {
      const refreshedProduct = products.value.find(
        (candidate) => String(candidate.id) === String(product.id),
      );
      const requirement = (refreshedProduct?.personnel_requirements ?? []).find(
        (candidate) => String(candidate.id) === String(draft.requirementId),
      );

      if (!requirement) {
        continue;
      }

      const currentSlot = getRequirementPayPeriod(requirement, targetPeriodId);
      const requirementPayload = {
        product_price_id: targetPeriodId,
        hourly_rate: draft.hourly_rate ?? 0,
        daily_rate: draft.daily_rate ?? 0,
        currency: draft.currency ?? "GBP",
      };

      if (currentSlot && !currentSlot.isNew) {
        updatedProduct = await productService.updateRequirementPayPeriod(
          currentSlot.id,
          requirementPayload,
        );
      } else {
        updatedProduct = await productService.createRequirementPayPeriod(
          requirement.id,
          requirementPayload,
        );
      }

      replaceProduct(updatedProduct);
    }

    showToast(
      dirtyRequirementDrafts.length > 0
        ? "Price schedule and staff pay saved"
        : period.dirty
          ? "Price schedule saved"
          : "Staff pay saved",
    );
  } catch (err) {
    console.error(err);
    error.value =
      err.response?.data?.message ??
      "Unable to save price period. Please check your input.";
  } finally {
    savingKey.value = null;
  }
};

const removePeriod = async (product, period) => {
  if (period.isNew) {
    product.price_periods = product.price_periods.filter(
      (p) => p.id !== period.id,
    );
    for (const requirement of product.personnel_requirements ?? []) {
      requirement.pay_periods = (requirement.pay_periods ?? []).filter(
        (p) => String(p.product_price_id) !== String(period.id),
      );
    }
    return;
  }

  deletingKey.value = getActionKey("product", period);
  error.value = null;

  try {
    const updated = await productService.deletePricePeriod(period.id);
    replaceProduct(updated);
    showToast("Price period removed");
  } catch (err) {
    console.error(err);
    error.value =
      err.response?.data?.message ??
      "Unable to delete price period. Please try again.";
  } finally {
    deletingKey.value = null;
  }
};

const removeRequirementPayPeriod = async (requirement, period) => {
  if (period.isNew) {
    period.hourly_rate = 0;
    period.daily_rate = 0;
    period.currency = period.currency ?? "GBP";
    period.dirty = false;
    return;
  }

  deletingKey.value = getActionKey("requirement", period);
  error.value = null;

  try {
    const updated = await productService.deleteRequirementPayPeriod(period.id);
    replaceProduct(updated);
    showToast("Staff pay period removed");
  } catch (err) {
    console.error(err);
    error.value =
      err.response?.data?.message ??
      "Unable to delete staff pay period. Please try again.";
  } finally {
    deletingKey.value = null;
  }
};

const getProductPricePeriod = (product, productPriceId) => {
  return (product?.price_periods ?? []).find(
    (period) => String(period.id) === String(productPriceId),
  );
};

const getRequirementPayPeriod = (requirement, productPriceId) => {
  return (
    (requirement?.pay_periods ?? []).find(
      (period) => String(period.product_price_id) === String(productPriceId),
    ) ?? null
  );
};

const updateRequirementPayField = (
  requirement,
  productPriceId,
  field,
  value,
) => {
  const slot = getRequirementPayPeriod(requirement, productPriceId);
  if (!slot) return;

  slot[field] = value;
  markDirty(slot);
};

const canPersistRequirementPayPeriod = (product, period) => {
  const linked = getProductPricePeriod(product, period.product_price_id);
  return Boolean(linked && !linked.isNew);
};

const saveLogisticsPricing = async () => {
  logisticsSaving.value = true;
  error.value = null;

  try {
    const response = await productService.updateLogisticsPricing({
      ...normalizeLogisticsPricing(logisticsPricing),
    });
    Object.assign(logisticsPricing, normalizeLogisticsPricing(response));
    logisticsModalOpen.value = false;
    showToast("Logistics pricing saved");
  } catch (err) {
    console.error(err);
    error.value =
      err.response?.data?.message ?? "Unable to save logistics pricing.";
  } finally {
    logisticsSaving.value = false;
  }
};

const todayDateKey = (() => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
})();

const isHistoricalPricePeriod = (period) =>
  Boolean(period?.ends_at && period.ends_at < todayDateKey);

const isCurrentPricePeriod = (period) =>
  Boolean(
    period?.starts_at &&
    period.starts_at <= todayDateKey &&
    (!period.ends_at || period.ends_at >= todayDateKey),
  );

const currentPricePeriod = (product) =>
  (product?.price_periods ?? []).find((period) => isCurrentPricePeriod(period));

const nextPricePeriod = (product) =>
  (product?.price_periods ?? []).find(
    (period) => period.starts_at && period.starts_at > todayDateKey,
  );

const latestPricePeriod = (product) => {
  const periods = product?.price_periods ?? [];
  return periods.length > 0 ? periods[periods.length - 1] : null;
};

const summaryPricePeriod = (product) =>
  currentPricePeriod(product) ??
  nextPricePeriod(product) ??
  latestPricePeriod(product);

const periodDateRange = (period) => {
  if (!period) return "No periods";
  return `${formatDate(period.starts_at)} - ${period.ends_at ? formatDate(period.ends_at) : "Open"}`;
};

const periodStatus = (product) => {
  const summary = summaryPricePeriod(product);
  if (!summary) return "Missing";
  if (isCurrentPricePeriod(summary)) return "Active";
  if (summary.starts_at && summary.starts_at > todayDateKey) return "Next";
  return "Historic";
};

const periodStatusClass = (product) => {
  const status = periodStatus(product);
  if (status === "Active") return "bg-emerald-50 text-emerald-700";
  if (status === "Next") return "bg-amber-50 text-amber-700";
  if (status === "Historic") return "bg-slate-100 text-slate-600";
  return "bg-rose-50 text-rose-700";
};

const isHistoryExpanded = (product) =>
  Boolean(expandedHistoryByProduct.value?.[product.id]);

const hiddenHistoricalCount = (product) =>
  (product?.price_periods ?? []).filter((period) =>
    isHistoricalPricePeriod(period),
  ).length;

const visiblePricePeriods = (product) => {
  const periods = product?.price_periods ?? [];
  if (isHistoryExpanded(product)) {
    return periods;
  }

  return periods.filter(
    (period) => period.dirty || !isHistoricalPricePeriod(period),
  );
};

const toggleHistory = (product) => {
  expandedHistoryByProduct.value = {
    ...expandedHistoryByProduct.value,
    [product.id]: !isHistoryExpanded(product),
  };
};

const productRequirements = (product) => product?.personnel_requirements ?? [];

const staffRequirementName = (requirement) =>
  requirement.skill?.name ?? `Skill #${requirement.staff_skill_id}`;

const personnelSummary = (product) => {
  const requirements = productRequirements(product);
  if (requirements.length === 0) return "No staff requirements";

  return requirements
    .map(
      (requirement) =>
        `${requirement.required_count ?? 1} x ${staffRequirementName(requirement)}`,
    )
    .join(", ");
};

const staffPayPreview = (product) => {
  const period = summaryPricePeriod(product);
  return productRequirements(product).map((requirement) => {
    const slot = period
      ? getRequirementPayPeriod(requirement, period.id)
      : null;
    return {
      id: requirement.id,
      label: `${requirement.required_count ?? 1} x ${staffRequirementName(requirement)}`,
      value: period
        ? formatRatePair(slot?.hourly_rate, slot?.daily_rate)
        : "No period",
    };
  });
};

const staffPayExtraCount = (product) =>
  Math.max(staffPayPreview(product).length - 3, 0);

const periodCountLabel = (product) => {
  const count = product?.price_periods?.length ?? 0;
  return `${count} period${count === 1 ? "" : "s"}`;
};

const isGlobalPricePeriod = (period) => Boolean(period?.pricing_date_range_id);

const formatPercentage = (value) =>
  `${Number(value ?? 0).toLocaleString("en-GB", {
    maximumFractionDigits: 3,
  })}%`;

const isoDateFromParts = (year, month, day) =>
  `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

const addYearsAndSubtractDay = (value) => {
  if (!isValidPricePeriodDate(value)) return "";
  const [year, month, day] = value.split("-").map(Number);
  const end = new Date(Date.UTC(year + 1, month - 1, day));
  end.setUTCDate(end.getUTCDate() - 1);
  return end.toISOString().slice(0, 10);
};

const suggestedAnnualStart = () => {
  const now = new Date();
  let year = now.getMonth() + 1 < 4 ? now.getFullYear() : now.getFullYear() + 1;
  const latestConfiguredStart = products.value
    .flatMap((product) => product.price_periods ?? [])
    .map((period) => period.starts_at)
    .filter(Boolean)
    .sort()
    .slice(-1)[0];

  while (
    latestConfiguredStart &&
    isoDateFromParts(year, 4, 1) <= latestConfiguredStart
  ) {
    year += 1;
  }

  return isoDateFromParts(year, 4, 1);
};

const openDateRangeModal = () => {
  dateRangeError.value = null;
  editingDateRangeId.value = null;
  newDateRange.starts_at = suggestedAnnualStart();
  newDateRange.ends_at = addYearsAndSubtractDay(newDateRange.starts_at);
  newDateRange.percentage_increase = 0;
  dateRangeModalOpen.value = true;
};

const openEditDateRangeModal = (dateRange) => {
  dateRangeError.value = null;
  editingDateRangeId.value = dateRange.id;
  newDateRange.starts_at = dateRange.starts_at;
  newDateRange.ends_at = dateRange.ends_at;
  newDateRange.percentage_increase = Number(dateRange.percentage_increase ?? 0);
  dateRangeModalOpen.value = true;
};

const isEditingDateRange = computed(() => Boolean(editingDateRangeId.value));

const updateNewDateRangeStart = (value) => {
  newDateRange.starts_at = value;
  newDateRange.ends_at = addYearsAndSubtractDay(value);
};

const savePricingDateRange = async () => {
  dateRangeError.value = null;

  if (
    !isValidPricePeriodDate(newDateRange.starts_at) ||
    !isValidPricePeriodDate(newDateRange.ends_at)
  ) {
    dateRangeError.value = "Enter a valid start and end date.";
    return;
  }

  if (newDateRange.ends_at < newDateRange.starts_at) {
    dateRangeError.value = "End date cannot be before the start date.";
    return;
  }

  const percentage = Number(newDateRange.percentage_increase);
  if (!Number.isFinite(percentage) || percentage < 0 || percentage > 100) {
    dateRangeError.value = "Percentage increase must be between 0% and 100%.";
    return;
  }

  dateRangeSaving.value = true;

  try {
    const response = isEditingDateRange.value
      ? await productService.updatePricingDateRange(editingDateRangeId.value, {
          percentage_increase: percentage,
        })
      : await productService.createPricingDateRange({
          starts_at: newDateRange.starts_at,
          ends_at: newDateRange.ends_at,
          percentage_increase: percentage,
        });

    products.value = (response.products ?? []).map((product) =>
      normalizeProduct(product),
    );
    const normalizedDateRange = normalizePricingDateRange(response.date_range);
    const existingDateRangeIndex = pricingDateRanges.value.findIndex(
      (dateRange) => String(dateRange.id) === String(normalizedDateRange.id),
    );
    if (existingDateRangeIndex === -1) {
      pricingDateRanges.value = [
        ...pricingDateRanges.value,
        normalizedDateRange,
      ].sort((a, b) => a.starts_at.localeCompare(b.starts_at));
    } else {
      pricingDateRanges.value[existingDateRangeIndex] = normalizedDateRange;
    }
    dateRangeModalOpen.value = false;
    showToast(
      isEditingDateRange.value
        ? `${periodDateRange(response.date_range)} recalculated at ${formatPercentage(percentage)}`
        : `${periodDateRange(response.date_range)} created for ${products.value.length} products`,
    );
  } catch (err) {
    console.error(err);
    const validationErrors = Object.values(err.response?.data?.errors ?? {})
      .flat()
      .filter(Boolean);
    dateRangeError.value =
      (validationErrors[0] ??
      err.response?.data?.message ??
      isEditingDateRange.value)
        ? "Unable to change the percentage increase."
        : "Unable to create the all-product date range.";
  } finally {
    dateRangeSaving.value = false;
  }
};

const priceHistoryMetricLabel = (metric) =>
  PRICE_HISTORY_METRICS[metric] ?? "Price";

const openPriceHistory = (product, metric = "hourly_rate") => {
  error.value = null;
  activePriceHistoryProductId.value = product.id;
  activePriceHistoryMetric.value = metric;
};

const ensureVehicleRequirement = (product) => {
  if (!product.vehicle_requirements) {
    product.vehicle_requirements = normalizeVehicleRequirement(
      null,
      product.id,
    );
  }

  return product.vehicle_requirements;
};

const vehicleRequirementSummary = (product) => {
  const requirement = product?.vehicle_requirements;
  if (
    !requirement?.vehicle_type ||
    Number(requirement.required_count ?? 0) <= 0
  ) {
    return "No vehicles";
  }

  return `${requirement.required_count} x ${requirement.vehicle_type}`;
};

const markVehicleRequirementDirty = (product) => {
  ensureVehicleRequirement(product).dirty = true;
};

const saveVehicleRequirement = async (product) => {
  const requirement = ensureVehicleRequirement(product);
  const vehicleTypeId = requirement.vehicle_type_id
    ? Number(requirement.vehicle_type_id)
    : null;
  const vehicleType = vehicleTypes.value.find(
    (type) => type.id === vehicleTypeId,
  );
  const requiredCount = Number(requirement.required_count ?? 0);

  if (vehicleTypeId && requiredCount < 1) {
    error.value =
      "Set a vehicle count of at least 1, or clear the vehicle type.";
    return;
  }

  if (!vehicleTypeId && requiredCount > 0) {
    error.value = "Set a vehicle type, or clear the vehicle count.";
    return;
  }

  savingVehicleProductId.value = product.id;
  error.value = null;

  try {
    const updated = await productService.updateVehicleRequirement(product.id, {
      vehicle_type_id: vehicleTypeId,
      vehicle_type: vehicleType?.name ?? "",
      required_count: requiredCount,
    });
    replaceProduct(updated);
    activeVehicleProductId.value = null;
    showToast(
      requiredCount > 0
        ? "Vehicle requirement saved"
        : "Vehicle requirement cleared",
    );
  } catch (err) {
    console.error(err);
    error.value =
      err.response?.data?.message ?? "Unable to save vehicle requirement.";
  } finally {
    savingVehicleProductId.value = null;
  }
};

const clearVehicleRequirement = async (product) => {
  const requirement = ensureVehicleRequirement(product);
  requirement.vehicle_type_id = "";
  requirement.vehicle_type = "";
  requirement.required_count = 0;
  requirement.dirty = true;
  await saveVehicleRequirement(product);
};

const periodTableColspan = (product) =>
  8 + productRequirements(product).length * 2;

const activePricingProduct = computed(
  () =>
    products.value.find(
      (product) => String(product.id) === String(activePricingProductId.value),
    ) ?? null,
);

const activeVehicleProduct = computed(
  () =>
    products.value.find(
      (product) => String(product.id) === String(activeVehicleProductId.value),
    ) ?? null,
);

const activePriceHistoryProduct = computed(
  () =>
    products.value.find(
      (product) =>
        String(product.id) === String(activePriceHistoryProductId.value),
    ) ?? null,
);

const activePriceHistoryMetricLabel = computed(() =>
  priceHistoryMetricLabel(activePriceHistoryMetric.value),
);

const openPricingModal = (product) => {
  error.value = null;
  activePricingProductId.value = product.id;
};

const openVehicleModal = (product) => {
  error.value = null;
  ensureVehicleRequirement(product);
  activeVehicleProductId.value = product.id;
};

const openLogisticsModal = () => {
  error.value = null;
  logisticsModalOpen.value = true;
};

onMounted(loadProducts);
onBeforeUnmount(() => {
  if (toastTimeout) {
    clearTimeout(toastTimeout);
  }
});
</script>

<template>
  <div class="pricing-manager space-y-6">
    <div
      class="box flex flex-col gap-3 md:flex-row md:items-center md:justify-between"
    >
      <div>
        <h1 class="text-2xl font-semibold">Pricing</h1>
        <p class="text-sm text-slate-500">
          Compare product rates, staff pay, vehicle requirements, and logistics
          rates.
        </p>
      </div>
      <div class="flex flex-wrap gap-2 self-start md:self-auto">
        <button
          class="btn accent flex items-center gap-2"
          type="button"
          :disabled="loading || Boolean(error) || products.length === 0"
          @click="openDateRangeModal"
        >
          <PlusCircleIcon class="h-4 w-4" />
          Add annual period
        </button>
        <button
          class="btn flex items-center gap-2"
          type="button"
          @click="openLogisticsModal"
        >
          <PencilSquareIcon class="h-4 w-4" />
          Logistics rates
        </button>
      </div>
    </div>

    <div v-if="loading" class="box text-center text-slate-500">
      Loading product pricing...
    </div>
    <div v-else-if="error" class="box text-center text-rose-500">
      {{ error }}
    </div>

    <div v-if="!loading && !error" class="box space-y-4">
      <div
        class="flex flex-col gap-1 md:flex-row md:items-end md:justify-between"
      >
        <div>
          <h2 class="text-lg font-semibold">All-product date ranges</h2>
          <p class="text-sm text-slate-500">
            Annual periods created here are added to every product in one go.
          </p>
        </div>
        <div class="text-xs text-slate-500">
          {{ pricingDateRanges.length }} centrally managed
        </div>
      </div>

      <div
        v-if="pricingDateRanges.length === 0"
        class="rounded-lg border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500"
      >
        No all-product periods have been created yet. Existing product periods
        remain unchanged.
      </div>
      <div v-else class="overflow-x-auto rounded-lg border border-slate-200">
        <table class="min-w-[620px] w-full text-sm">
          <thead
            class="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
          >
            <tr>
              <th class="border-b border-slate-200 px-3 py-2">Date range</th>
              <th class="border-b border-slate-200 px-3 py-2 text-right">
                Price and pay increase
              </th>
              <th class="border-b border-slate-200 px-3 py-2 text-right">
                Product coverage
              </th>
              <th class="border-b border-slate-200 px-3 py-2 text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100">
            <tr v-for="dateRange in pricingDateRanges" :key="dateRange.id">
              <td class="px-3 py-2 font-medium text-slate-800">
                {{ periodDateRange(dateRange) }}
              </td>
              <td class="px-3 py-2 text-right font-mono text-slate-800">
                {{ formatPercentage(dateRange.percentage_increase) }}
              </td>
              <td class="px-3 py-2 text-right">
                <span
                  class="rounded px-2 py-0.5 text-xs font-medium"
                  :class="
                    dateRange.applies_to_all_products
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-amber-50 text-amber-700'
                  "
                >
                  {{ dateRange.product_prices_count }} /
                  {{ dateRange.product_count }}
                  products
                </span>
              </td>
              <td class="px-3 py-2 text-right">
                <button
                  class="btn px-3 py-1 text-xs"
                  type="button"
                  @click="openEditDateRangeModal(dateRange)"
                >
                  Change increase
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="!loading && !error" class="box space-y-4">
      <div
        class="flex flex-col gap-1 md:flex-row md:items-end md:justify-between"
      >
        <div>
          <h2 class="text-lg font-semibold">Product rate table</h2>
          <p class="text-sm text-slate-500">
            Current period is shown first; if no current period exists the next
            or latest period is shown.
          </p>
        </div>
        <div class="text-xs text-slate-500">{{ products.length }} products</div>
      </div>

      <div class="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table class="min-w-[1180px] w-full border-collapse text-sm">
          <thead
            class="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
          >
            <tr>
              <th class="border-b border-slate-200 px-3 py-2">Product</th>
              <th class="border-b border-slate-200 px-3 py-2">Shown Period</th>
              <th class="border-b border-slate-200 px-3 py-2 text-right">
                Fixed
              </th>
              <th class="border-b border-slate-200 px-3 py-2 text-right">
                Sell Hr
              </th>
              <th class="border-b border-slate-200 px-3 py-2 text-right">
                Sell Day
              </th>
              <th class="border-b border-slate-200 px-3 py-2 text-right">
                Internal Hr
              </th>
              <th class="border-b border-slate-200 px-3 py-2 text-right">
                Internal Day
              </th>
              <th class="border-b border-slate-200 px-3 py-2">Staff Pay</th>
              <th class="border-b border-slate-200 px-3 py-2">Vehicles</th>
              <th class="border-b border-slate-200 px-3 py-2">Setup</th>
              <th class="border-b border-slate-200 px-3 py-2 text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100">
            <tr v-if="products.length === 0">
              <td colspan="11" class="px-3 py-8 text-center text-slate-500">
                No products found.
              </td>
            </tr>
            <tr
              v-for="product in products"
              :key="product.id"
              class="align-top hover:bg-slate-50/70"
            >
              <td class="w-56 px-3 py-3">
                <button
                  class="group w-full rounded px-1 py-0.5 text-left hover:bg-cyan-50 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  type="button"
                  title="View price history"
                  @click="openPriceHistory(product)"
                >
                  <span
                    class="font-medium text-slate-900 group-hover:text-cyan-800"
                  >
                    {{ product.name }}
                  </span>
                  <span
                    class="mt-1 flex items-center gap-1 text-xs text-slate-500"
                  >
                    <ChartBarSquareIcon class="h-4 w-4" />
                    {{ product.short }} · View price history
                  </span>
                </button>
              </td>
              <td class="w-48 px-3 py-3">
                <div class="flex items-center gap-2">
                  <span
                    class="rounded px-2 py-0.5 text-[11px] font-semibold"
                    :class="periodStatusClass(product)"
                  >
                    {{ periodStatus(product) }}
                  </span>
                </div>
                <div class="mt-1 text-xs text-slate-600">
                  {{ periodDateRange(summaryPricePeriod(product)) }}
                </div>
              </td>
              <td class="px-3 py-3 text-right">
                <button
                  class="w-full rounded px-1 py-0.5 text-right font-mono text-slate-800 hover:bg-cyan-50 hover:text-cyan-800 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  type="button"
                  :title="`Show ${priceHistoryMetricLabel('fixed_fee')} history`"
                  @click="openPriceHistory(product, 'fixed_fee')"
                >
                  {{ formatMoney(summaryPricePeriod(product)?.fixed_fee) }}
                </button>
              </td>
              <td class="px-3 py-3 text-right">
                <button
                  class="w-full rounded px-1 py-0.5 text-right font-mono text-slate-800 hover:bg-cyan-50 hover:text-cyan-800 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  type="button"
                  :title="`Show ${priceHistoryMetricLabel('hourly_rate')} history`"
                  @click="openPriceHistory(product, 'hourly_rate')"
                >
                  {{ formatMoney(summaryPricePeriod(product)?.hourly_rate) }}
                </button>
              </td>
              <td class="px-3 py-3 text-right">
                <button
                  class="w-full rounded px-1 py-0.5 text-right font-mono text-slate-800 hover:bg-cyan-50 hover:text-cyan-800 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  type="button"
                  :title="`Show ${priceHistoryMetricLabel('daily_rate')} history`"
                  @click="openPriceHistory(product, 'daily_rate')"
                >
                  {{ formatMoney(summaryPricePeriod(product)?.daily_rate) }}
                </button>
              </td>
              <td class="px-3 py-3 text-right">
                <button
                  class="w-full rounded px-1 py-0.5 text-right font-mono text-slate-800 hover:bg-cyan-50 hover:text-cyan-800 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  type="button"
                  :title="`Show ${priceHistoryMetricLabel('internal_hourly_rate')} history`"
                  @click="openPriceHistory(product, 'internal_hourly_rate')"
                >
                  {{
                    formatMoney(
                      summaryPricePeriod(product)?.internal_hourly_rate,
                    )
                  }}
                </button>
              </td>
              <td class="px-3 py-3 text-right">
                <button
                  class="w-full rounded px-1 py-0.5 text-right font-mono text-slate-800 hover:bg-cyan-50 hover:text-cyan-800 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  type="button"
                  :title="`Show ${priceHistoryMetricLabel('internal_daily_rate')} history`"
                  @click="openPriceHistory(product, 'internal_daily_rate')"
                >
                  {{
                    formatMoney(
                      summaryPricePeriod(product)?.internal_daily_rate,
                    )
                  }}
                </button>
              </td>
              <td class="min-w-[230px] px-3 py-3">
                <div
                  v-if="productRequirements(product).length === 0"
                  class="text-xs text-slate-500"
                >
                  Direct internal pay only
                </div>
                <div v-else class="space-y-1">
                  <div
                    v-for="item in staffPayPreview(product).slice(0, 3)"
                    :key="item.id"
                    class="flex items-start justify-between gap-3 text-xs"
                  >
                    <span class="max-w-[9rem] truncate text-slate-600">{{
                      item.label
                    }}</span>
                    <span class="whitespace-nowrap font-mono text-slate-800">{{
                      item.value
                    }}</span>
                  </div>
                  <div
                    v-if="staffPayExtraCount(product) > 0"
                    class="text-xs text-slate-400"
                  >
                    +{{ staffPayExtraCount(product) }} more
                  </div>
                </div>
              </td>
              <td class="w-52 px-3 py-3">
                <button
                  class="inline-flex max-w-full items-center gap-2 rounded border border-slate-200 px-2 py-1 text-left text-xs text-slate-700 hover:border-slate-300 hover:bg-white"
                  type="button"
                  @click="openVehicleModal(product)"
                >
                  <TruckIcon class="h-4 w-4 shrink-0 text-slate-500" />
                  <span class="truncate">{{
                    vehicleRequirementSummary(product)
                  }}</span>
                </button>
              </td>
              <td class="min-w-[220px] px-3 py-3">
                <div class="text-xs text-slate-600">
                  {{ personnelSummary(product) }}
                </div>
                <div
                  class="mt-1 flex items-center gap-2 text-xs text-slate-400"
                >
                  <CalendarDaysIcon class="h-4 w-4" />
                  {{ periodCountLabel(product) }}
                </div>
              </td>
              <td class="w-44 px-3 py-3 text-right">
                <div class="flex justify-end gap-2">
                  <button
                    class="btn flex items-center gap-1 px-3 py-1 text-xs"
                    type="button"
                    @click="openPricingModal(product)"
                  >
                    <PencilSquareIcon class="h-4 w-4" />
                    Edit pricing
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <div
    v-if="dateRangeModalOpen"
    class="pricing-manager fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4"
    @click.self="dateRangeModalOpen = false"
  >
    <div class="w-full max-w-2xl rounded-lg bg-white shadow-xl">
      <div
        class="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4"
      >
        <div>
          <h2 class="text-lg font-semibold">
            {{
              isEditingDateRange
                ? "Change percentage increase"
                : "Create an all-product period"
            }}
          </h2>
          <p class="text-sm text-slate-500">
            {{
              isEditingDateRange
                ? "Recalculate this period and any later all-product periods."
                : "Adds one aligned price period to every current product."
            }}
          </p>
        </div>
        <button
          class="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          type="button"
          aria-label="Close date range"
          :disabled="dateRangeSaving"
          @click="dateRangeModalOpen = false"
        >
          <XMarkIcon class="h-5 w-5" />
        </button>
      </div>

      <div class="space-y-5 px-5 py-5">
        <div
          v-if="dateRangeError"
          class="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
        >
          {{ dateRangeError }}
        </div>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label
              class="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500"
            >
              Start date
            </label>
            <input
              :value="newDateRange.starts_at"
              type="date"
              :min="todayDateKey"
              :disabled="isEditingDateRange"
              class="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
              @input="updateNewDateRangeStart($event.target.value)"
            />
          </div>
          <div>
            <label
              class="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500"
            >
              End date
            </label>
            <input
              v-model="newDateRange.ends_at"
              type="date"
              :min="newDateRange.starts_at"
              :disabled="isEditingDateRange"
              class="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
            />
            <p class="mt-1 text-xs text-slate-400">
              Defaults to the day before this date in the following year.
            </p>
          </div>
        </div>

        <div>
          <label
            class="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500"
          >
            Price and pay increase
          </label>
          <div class="relative w-44">
            <input
              v-model.number="newDateRange.percentage_increase"
              type="number"
              min="0"
              max="100"
              step="0.1"
              class="w-full rounded border border-slate-200 px-3 py-2 pr-9 text-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
            />
            <span
              class="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-slate-500"
            >
              %
            </span>
          </div>
        </div>

        <div class="rounded-lg bg-cyan-50 px-4 py-3 text-sm text-cyan-900">
          <p class="font-medium">
            <template v-if="isEditingDateRange">
              All {{ products.length }} product price periods will be
              recalculated from the preceding period.
            </template>
            <template v-else>
              This will create {{ products.length }} product price periods.
            </template>
          </p>
          <p class="mt-1 text-cyan-800">
            The percentage applies to every monetary value: fixed fees, sell
            rates, internal pay, and requirement-specific staff pay.
            <template v-if="isEditingDateRange">
              Set it to 0% to remove the uplift while keeping this date range.
              Sent and accepted quote snapshots are not changed.
            </template>
            <template v-else>
              Each product's previous period will end the day before the new
              one.
            </template>
          </p>
        </div>
      </div>

      <div class="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
        <button
          class="btn"
          type="button"
          :disabled="dateRangeSaving"
          @click="dateRangeModalOpen = false"
        >
          Cancel
        </button>
        <button
          class="btn accent"
          type="button"
          :disabled="dateRangeSaving || products.length === 0"
          @click="savePricingDateRange"
        >
          {{
            dateRangeSaving
              ? isEditingDateRange
                ? "Recalculating..."
                : "Creating..."
              : isEditingDateRange
                ? "Recalculate prices"
                : "Create for all products"
          }}
        </button>
      </div>
    </div>
  </div>

  <div
    v-if="activePriceHistoryProduct"
    class="pricing-manager fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4"
    @click.self="activePriceHistoryProductId = null"
  >
    <div
      class="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white shadow-xl"
    >
      <div
        class="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-slate-200 bg-white px-5 py-4"
      >
        <div>
          <p class="text-xs uppercase tracking-wide text-slate-500">
            {{ activePriceHistoryProduct.short }}
          </p>
          <h2 class="text-lg font-semibold">
            {{ activePriceHistoryProduct.name }}
          </h2>
          <p class="text-sm text-slate-500">
            {{ activePriceHistoryMetricLabel }} over configured price periods.
          </p>
        </div>
        <button
          class="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          type="button"
          aria-label="Close price history"
          @click="activePriceHistoryProductId = null"
        >
          <XMarkIcon class="h-5 w-5" />
        </button>
      </div>

      <div class="space-y-4 px-5 py-5">
        <div class="flex flex-wrap gap-2">
          <button
            v-for="(label, metric) in PRICE_HISTORY_METRICS"
            :key="metric"
            class="rounded border px-3 py-1 text-xs font-medium"
            :class="
              activePriceHistoryMetric === metric
                ? 'border-cyan-700 bg-cyan-50 text-cyan-800'
                : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
            "
            type="button"
            @click="activePriceHistoryMetric = metric"
          >
            {{ label }}
          </button>
        </div>

        <div v-if="(activePriceHistoryProduct.price_periods ?? []).length > 0">
          <PricingPeriodPriceChart
            :periods="activePriceHistoryProduct.price_periods"
            :metric="activePriceHistoryMetric"
            :metric-label="activePriceHistoryMetricLabel"
          />
        </div>
        <div v-else class="py-10 text-center text-sm text-slate-500">
          No price periods configured yet.
        </div>

        <div
          v-if="(activePriceHistoryProduct.price_periods ?? []).length > 0"
          class="overflow-x-auto rounded-lg border border-slate-200"
        >
          <table class="min-w-[520px] w-full text-sm">
            <thead
              class="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
            >
              <tr>
                <th class="border-b border-slate-200 px-3 py-2">Start</th>
                <th class="border-b border-slate-200 px-3 py-2">End</th>
                <th class="border-b border-slate-200 px-3 py-2 text-right">
                  {{ activePriceHistoryMetricLabel }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100">
              <tr
                v-for="period in activePriceHistoryProduct.price_periods"
                :key="`history-${period.id}`"
              >
                <td class="px-3 py-2 text-slate-700">
                  {{ formatDate(period.starts_at) }}
                </td>
                <td class="px-3 py-2 text-slate-700">
                  {{ period.ends_at ? formatDate(period.ends_at) : "Open" }}
                </td>
                <td class="px-3 py-2 text-right font-mono text-slate-900">
                  {{ formatMoney(period?.[activePriceHistoryMetric]) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="logisticsModalOpen"
    class="pricing-manager fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4"
    @click.self="logisticsModalOpen = false"
  >
    <div
      class="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white shadow-xl"
    >
      <div
        class="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-slate-200 bg-white px-5 py-4"
      >
        <div>
          <h2 class="text-lg font-semibold">Logistics pricing</h2>
          <p class="text-sm text-slate-500">
            Global transport, accommodation, admin, and base address rates.
          </p>
        </div>
        <button
          class="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          type="button"
          aria-label="Close logistics pricing"
          @click="logisticsModalOpen = false"
        >
          <XMarkIcon class="h-5 w-5" />
        </button>
      </div>

      <div class="space-y-4 px-5 py-5">
        <div>
          <label
            class="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500"
          >
            Company Base Address
          </label>
          <textarea
            v-model="logisticsPricing.base_address"
            rows="3"
            class="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
          />
        </div>

        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label
              class="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500"
            >
              Fuel Rate Per Mile
            </label>
            <CurrencyInput
              :price="logisticsPricing.fuel_rate_per_mile"
              @update:price="
                (value) => {
                  logisticsPricing.fuel_rate_per_mile = value;
                }
              "
            />
          </div>
          <div>
            <label
              class="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500"
            >
              Travel Driver Hourly Rate
            </label>
            <CurrencyInput
              :price="logisticsPricing.travel_driver_hourly_rate"
              @update:price="
                (value) => {
                  logisticsPricing.travel_driver_hourly_rate = value;
                }
              "
            />
          </div>
          <div>
            <label
              class="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500"
            >
              Accommodation Room Rate
            </label>
            <CurrencyInput
              :price="logisticsPricing.accommodation_room_nightly_rate"
              @update:price="
                (value) => {
                  logisticsPricing.accommodation_room_nightly_rate = value;
                }
              "
            />
          </div>
          <div>
            <label
              class="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500"
            >
              One-Off Admin Fee
            </label>
            <CurrencyInput
              :price="logisticsPricing.admin_fee"
              @update:price="
                (value) => {
                  logisticsPricing.admin_fee = value;
                }
              "
            />
          </div>
        </div>
      </div>

      <div class="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
        <button class="btn" type="button" @click="logisticsModalOpen = false">
          Cancel
        </button>
        <button
          class="btn accent"
          type="button"
          :disabled="logisticsSaving"
          @click="saveLogisticsPricing"
        >
          {{ logisticsSaving ? "Saving..." : "Save logistics" }}
        </button>
      </div>
    </div>
  </div>

  <div
    v-if="activeVehicleProduct"
    class="pricing-manager fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4"
    @click.self="activeVehicleProductId = null"
  >
    <div class="w-full max-w-lg rounded-lg bg-white shadow-xl">
      <div
        class="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4"
      >
        <div>
          <h2 class="text-lg font-semibold">Vehicle requirement</h2>
          <p class="text-sm text-slate-500">{{ activeVehicleProduct.name }}</p>
        </div>
        <button
          class="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          type="button"
          aria-label="Close vehicle requirement"
          @click="activeVehicleProductId = null"
        >
          <XMarkIcon class="h-5 w-5" />
        </button>
      </div>

      <div class="space-y-4 px-5 py-5">
        <div>
          <label
            class="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500"
          >
            Vehicle Type
          </label>
          <select
            v-model="activeVehicleProduct.vehicle_requirements.vehicle_type_id"
            class="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
            @change="markVehicleRequirementDirty(activeVehicleProduct)"
          >
            <option value="">No vehicle</option>
            <option
              v-for="vehicleType in vehicleTypes"
              :key="vehicleType.id"
              :value="vehicleType.id"
              :disabled="
                !vehicleType.active &&
                Number(
                  activeVehicleProduct.vehicle_requirements.vehicle_type_id,
                ) !== vehicleType.id
              "
            >
              {{ vehicleType.name
              }}{{ vehicleType.active ? "" : " (inactive)" }}
            </option>
          </select>
        </div>
        <div>
          <label
            class="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500"
          >
            Required Count
          </label>
          <input
            v-model.number="
              activeVehicleProduct.vehicle_requirements.required_count
            "
            type="number"
            min="0"
            max="999"
            class="w-36 rounded border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
            @input="markVehicleRequirementDirty(activeVehicleProduct)"
          />
        </div>
      </div>

      <div
        class="flex flex-wrap justify-between gap-2 border-t border-slate-200 px-5 py-4"
      >
        <button
          class="text-sm font-medium text-rose-700 disabled:text-slate-400"
          type="button"
          :disabled="savingVehicleProductId === activeVehicleProduct.id"
          @click="clearVehicleRequirement(activeVehicleProduct)"
        >
          Clear requirement
        </button>
        <div class="flex gap-2">
          <button
            class="btn"
            type="button"
            @click="activeVehicleProductId = null"
          >
            Cancel
          </button>
          <button
            class="btn accent"
            type="button"
            :disabled="savingVehicleProductId === activeVehicleProduct.id"
            @click="saveVehicleRequirement(activeVehicleProduct)"
          >
            {{
              savingVehicleProductId === activeVehicleProduct.id
                ? "Saving..."
                : "Save vehicle"
            }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="activePricingProduct"
    class="pricing-manager fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-3"
    @click.self="activePricingProductId = null"
  >
    <div
      class="max-h-[92vh] w-full max-w-[96rem] overflow-hidden rounded-lg bg-white shadow-xl"
    >
      <div
        class="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4"
      >
        <div>
          <p class="text-xs uppercase tracking-wide text-slate-500">
            {{ activePricingProduct.short }}
          </p>
          <h2 class="text-lg font-semibold">{{ activePricingProduct.name }}</h2>
          <p class="text-sm text-slate-500">
            Edit sell rates, internal pay, and staff pay in one grid.
          </p>
        </div>
        <div class="flex items-center gap-2">
          <button
            v-if="hiddenHistoricalCount(activePricingProduct) > 0"
            class="btn px-3 py-1 text-xs"
            type="button"
            @click="toggleHistory(activePricingProduct)"
          >
            {{
              isHistoryExpanded(activePricingProduct)
                ? "Hide previous"
                : "Show previous"
            }}
            ({{ hiddenHistoricalCount(activePricingProduct) }})
          </button>
          <button
            class="btn accent flex items-center gap-2 px-3 py-1 text-xs"
            type="button"
            @click="addPeriod(activePricingProduct)"
          >
            <PlusCircleIcon class="h-4 w-4" />
            Add period
          </button>
          <button
            class="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            type="button"
            aria-label="Close pricing editor"
            @click="activePricingProductId = null"
          >
            <XMarkIcon class="h-5 w-5" />
          </button>
        </div>
      </div>

      <div
        v-if="
          hiddenHistoricalCount(activePricingProduct) > 0 &&
          !isHistoryExpanded(activePricingProduct)
        "
        class="border-b border-slate-100 px-5 py-2 text-xs text-slate-500"
      >
        Showing current and future periods. Previous periods are collapsed.
      </div>

      <div class="max-h-[calc(92vh-8rem)] overflow-auto">
        <table class="min-w-[1120px] w-full border-collapse text-xs">
          <thead
            class="sticky top-0 z-10 bg-slate-50 text-left font-semibold uppercase tracking-wide text-slate-500"
          >
            <tr>
              <th
                rowspan="2"
                class="border-b border-r border-slate-200 px-2 py-2"
              >
                Start
              </th>
              <th
                rowspan="2"
                class="border-b border-r border-slate-200 px-2 py-2"
              >
                End
              </th>
              <th
                colspan="3"
                class="border-b border-r border-slate-200 px-2 py-2 text-center"
              >
                Sell Rates
              </th>
              <th
                colspan="2"
                class="border-b border-r border-slate-200 px-2 py-2 text-center"
              >
                Internal Pay
              </th>
              <th
                v-if="productRequirements(activePricingProduct).length > 0"
                :colspan="productRequirements(activePricingProduct).length * 2"
                class="border-b border-r border-slate-200 px-2 py-2 text-center"
              >
                Staff Pay By Requirement
              </th>
              <th
                rowspan="2"
                class="border-b border-slate-200 px-2 py-2 text-right"
              >
                Actions
              </th>
            </tr>
            <tr>
              <th
                class="border-b border-r border-slate-200 px-2 py-2 text-right"
              >
                Fixed
              </th>
              <th
                class="border-b border-r border-slate-200 px-2 py-2 text-right"
              >
                Hourly
              </th>
              <th
                class="border-b border-r border-slate-200 px-2 py-2 text-right"
              >
                Daily
              </th>
              <th
                class="border-b border-r border-slate-200 px-2 py-2 text-right"
              >
                Hourly
              </th>
              <th
                class="border-b border-r border-slate-200 px-2 py-2 text-right"
              >
                Daily
              </th>
              <template
                v-for="requirement in productRequirements(activePricingProduct)"
                :key="`heading-${requirement.id}`"
              >
                <th
                  class="min-w-[8rem] border-b border-r border-slate-200 px-2 py-2 text-right"
                >
                  {{ staffRequirementName(requirement) }} Hr
                </th>
                <th
                  class="min-w-[8rem] border-b border-r border-slate-200 px-2 py-2 text-right"
                >
                  {{ staffRequirementName(requirement) }} Day
                </th>
              </template>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100">
            <tr v-if="visiblePricePeriods(activePricingProduct).length === 0">
              <td
                :colspan="periodTableColspan(activePricingProduct)"
                class="px-4 py-8 text-center text-sm text-slate-500"
              >
                <template
                  v-if="hiddenHistoricalCount(activePricingProduct) > 0"
                >
                  No current or future periods. Use "Show previous" to view
                  historic rates.
                </template>
                <template v-else>No price periods configured yet.</template>
              </td>
            </tr>
            <tr
              v-for="period in visiblePricePeriods(activePricingProduct)"
              :key="`period-${period.id}`"
              class="align-top hover:bg-slate-50/70"
            >
              <td class="min-w-[9rem] border-r border-slate-100 px-2 py-2">
                <input
                  :value="period.starts_at_draft"
                  type="date"
                  :min="MIN_PRICE_PERIOD_DATE"
                  :disabled="isGlobalPricePeriod(period)"
                  :title="
                    isGlobalPricePeriod(period)
                      ? 'This date is managed by the all-product period'
                      : ''
                  "
                  class="w-full rounded border border-slate-200 px-2 py-1 text-xs focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400 disabled:bg-slate-100 disabled:text-slate-500"
                  @input="
                    updateDateDraft(period, 'starts_at', $event.target.value)
                  "
                  @change="commitDateDraft(period, 'starts_at')"
                  @blur="commitDateDraft(period, 'starts_at')"
                />
              </td>
              <td class="min-w-[9rem] border-r border-slate-100 px-2 py-2">
                <input
                  :value="period.ends_at_draft"
                  type="date"
                  :min="MIN_PRICE_PERIOD_DATE"
                  :disabled="isGlobalPricePeriod(period)"
                  :title="
                    isGlobalPricePeriod(period)
                      ? 'This date is managed by the all-product period'
                      : ''
                  "
                  class="w-full rounded border border-slate-200 px-2 py-1 text-xs focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400 disabled:bg-slate-100 disabled:text-slate-500"
                  @input="
                    updateDateDraft(period, 'ends_at', $event.target.value)
                  "
                  @change="commitDateDraft(period, 'ends_at')"
                  @blur="commitDateDraft(period, 'ends_at')"
                />
              </td>
              <td class="min-w-[7rem] border-r border-slate-100 px-2 py-2">
                <CurrencyInput
                  :price="period.fixed_fee"
                  @update:price="
                    (value) => {
                      period.fixed_fee = value;
                      markDirty(period);
                    }
                  "
                />
              </td>
              <td class="min-w-[7rem] border-r border-slate-100 px-2 py-2">
                <CurrencyInput
                  :price="period.hourly_rate"
                  @update:price="
                    (value) => {
                      period.hourly_rate = value;
                      markDirty(period);
                    }
                  "
                />
              </td>
              <td class="min-w-[7rem] border-r border-slate-100 px-2 py-2">
                <CurrencyInput
                  :price="period.daily_rate"
                  @update:price="
                    (value) => {
                      period.daily_rate = value;
                      markDirty(period);
                    }
                  "
                />
              </td>
              <td class="min-w-[7rem] border-r border-slate-100 px-2 py-2">
                <CurrencyInput
                  :price="period.internal_hourly_rate"
                  @update:price="
                    (value) => {
                      period.internal_hourly_rate = value;
                      markDirty(period);
                    }
                  "
                />
              </td>
              <td class="min-w-[7rem] border-r border-slate-100 px-2 py-2">
                <CurrencyInput
                  :price="period.internal_daily_rate"
                  @update:price="
                    (value) => {
                      period.internal_daily_rate = value;
                      markDirty(period);
                    }
                  "
                />
              </td>
              <template
                v-for="requirement in productRequirements(activePricingProduct)"
                :key="`slot-${period.id}-${requirement.id}`"
              >
                <td class="min-w-[8rem] border-r border-slate-100 px-2 py-2">
                  <CurrencyInput
                    :price="
                      getRequirementPayPeriod(requirement, period.id)
                        ?.hourly_rate ?? 0
                    "
                    @update:price="
                      (value) =>
                        updateRequirementPayField(
                          requirement,
                          period.id,
                          'hourly_rate',
                          value,
                        )
                    "
                  />
                </td>
                <td class="min-w-[8rem] border-r border-slate-100 px-2 py-2">
                  <CurrencyInput
                    :price="
                      getRequirementPayPeriod(requirement, period.id)
                        ?.daily_rate ?? 0
                    "
                    @update:price="
                      (value) =>
                        updateRequirementPayField(
                          requirement,
                          period.id,
                          'daily_rate',
                          value,
                        )
                    "
                  />
                  <button
                    v-if="getRequirementPayPeriod(requirement, period.id)"
                    class="mt-1 text-[11px] font-medium text-rose-700 disabled:text-slate-400"
                    type="button"
                    :disabled="
                      isSaving('product', period) ||
                      isDeleting(
                        'requirement',
                        getRequirementPayPeriod(requirement, period.id),
                      )
                    "
                    @click="
                      removeRequirementPayPeriod(
                        requirement,
                        getRequirementPayPeriod(requirement, period.id),
                      )
                    "
                  >
                    {{
                      getRequirementPayPeriod(requirement, period.id).isNew
                        ? "Clear"
                        : "Remove"
                    }}
                  </button>
                  <div
                    v-if="
                      getRequirementPayPeriod(requirement, period.id) &&
                      !canPersistRequirementPayPeriod(
                        activePricingProduct,
                        getRequirementPayPeriod(requirement, period.id),
                      )
                    "
                    class="mt-1 text-[11px] text-slate-400"
                  >
                    Saves with row
                  </div>
                </td>
              </template>
              <td class="w-40 px-2 py-2 text-right">
                <div class="flex flex-col items-end gap-2">
                  <button
                    class="btn accent w-full px-3 py-1 text-xs"
                    type="button"
                    :disabled="
                      isSaving('product', period) ||
                      !canSavePeriodRow(activePricingProduct, period)
                    "
                    @click="savePeriod(activePricingProduct, period)"
                  >
                    {{ period.isNew ? "Create + save" : "Save row" }}
                  </button>
                  <div
                    v-if="isGlobalPricePeriod(period)"
                    class="w-full rounded bg-cyan-50 px-2 py-1 text-center text-[11px] font-medium text-cyan-800"
                  >
                    All-product period
                  </div>
                  <button
                    v-else
                    class="btn danger flex w-full items-center justify-center gap-1 px-3 py-1 text-xs"
                    type="button"
                    :disabled="
                      isSaving('product', period) ||
                      isDeleting('product', period)
                    "
                    @click="removePeriod(activePricingProduct, period)"
                  >
                    <TrashIcon class="h-4 w-4" />
                    Remove
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <div
    v-if="toast.visible"
    class="pricing-manager fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium text-white shadow-xl"
    :class="toast.tone === 'error' ? 'bg-rose-600' : 'bg-emerald-600'"
  >
    <span>{{ toast.message }}</span>
  </div>
</template>

<style>
@media (prefers-color-scheme: dark) {
  .pricing-manager {
    color: #e2e8f0;
  }

  .pricing-manager .bg-white {
    background-color: #1e293b;
  }

  .pricing-manager .bg-slate-50,
  .pricing-manager .bg-slate-50\/70 {
    background-color: rgba(15, 23, 42, 0.55);
  }

  .pricing-manager .bg-slate-100,
  .pricing-manager .disabled\:bg-slate-100:disabled {
    background-color: #334155;
  }

  .pricing-manager .text-slate-900,
  .pricing-manager .text-slate-800 {
    color: #f1f5f9;
  }

  .pricing-manager .text-slate-700,
  .pricing-manager .text-slate-600 {
    color: #cbd5e1;
  }

  .pricing-manager .text-slate-500,
  .pricing-manager .text-slate-400,
  .pricing-manager .disabled\:text-slate-400:disabled,
  .pricing-manager .disabled\:text-slate-500:disabled {
    color: #94a3b8;
  }

  .pricing-manager .border-slate-100,
  .pricing-manager .border-slate-200,
  .pricing-manager .border-slate-300 {
    border-color: #475569;
  }

  .pricing-manager .divide-slate-100 > :not([hidden]) ~ :not([hidden]) {
    border-color: #334155;
  }

  .pricing-manager .bg-cyan-50 {
    background-color: rgba(8, 145, 178, 0.15);
  }

  .pricing-manager .text-cyan-900,
  .pricing-manager .text-cyan-800,
  .pricing-manager .text-cyan-700 {
    color: #67e8f9;
  }

  .pricing-manager .bg-emerald-50 {
    background-color: rgba(16, 185, 129, 0.15);
  }

  .pricing-manager .text-emerald-700 {
    color: #6ee7b7;
  }

  .pricing-manager .bg-amber-50 {
    background-color: rgba(245, 158, 11, 0.15);
  }

  .pricing-manager .text-amber-700 {
    color: #fcd34d;
  }

  .pricing-manager .bg-rose-50 {
    background-color: rgba(244, 63, 94, 0.15);
  }

  .pricing-manager .border-rose-200 {
    border-color: #9f1239;
  }

  .pricing-manager .text-rose-700,
  .pricing-manager .text-rose-500 {
    color: #fda4af;
  }

  .pricing-manager input,
  .pricing-manager select,
  .pricing-manager textarea,
  .pricing-manager option {
    border-color: #475569;
    background-color: #0f172a;
    color: #f1f5f9;
    color-scheme: dark;
  }

  .pricing-manager .hover\:bg-white:hover,
  .pricing-manager .hover\:bg-slate-50:hover,
  .pricing-manager .hover\:bg-slate-100:hover,
  .pricing-manager .hover\:bg-slate-50\/70:hover {
    background-color: rgba(15, 23, 42, 0.8);
  }

  .pricing-manager .hover\:bg-cyan-50:hover {
    background-color: rgba(8, 145, 178, 0.15);
  }

  .pricing-manager .group:hover .group-hover\:text-cyan-800 {
    color: #67e8f9;
  }

  .pricing-manager .hover\:text-slate-700:hover {
    color: #e2e8f0;
  }

  .pricing-manager .hover\:text-cyan-800:hover {
    color: #67e8f9;
  }
}
</style>
